import React, { useEffect, useState, useContext, useRef } from "react";
import { Edit2, Users, FolderOpen, Crown } from "lucide-react";
import { CameraAlt } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { SessionContext } from "../../context/sessionContext";
import {
  plans,
  oneTimeStudy,
  studentPlan,
} from "../../assets/data/pricingData";
import { useUser } from "../../context/UserContext";
import Cookies from "js-cookie";

const Profile = () => {
  const { session } = useContext(SessionContext);
  const [planDetails, setPlanDetails] = useState(null);
  const {
    userData,
    profilePicUrl,
    updateUser,
    refreshUser,
    roleName,
    orgId,
    isOrgDeactivated,
    setIsOrgDeactivated,
  } = useUser();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [isUploadingPic, setIsUploadingPic] = useState(false);
  const [nameError, setNameError] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivationError, setDeactivationError] = useState("");
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [isOrgDeactivating, setIsOrgDeactivating] = useState(false);
  const [isCancelingOrgDeactivation, setIsCancelingOrgDeactivation] =
    useState(false);
  const [orgDeactivationError, setOrgDeactivationError] = useState("");
  const [showDeactivateOrgDialog, setShowDeactivateOrgDialog] = useState(false);
  const [showCancelOrgDialog, setShowCancelOrgDialog] = useState(false);
  const fileInputRef = useRef(null);
  const NAME_MAX_LENGTH = 40;

  const findPlanByName = (planName) => {
    if (!planName) return null;
    const main = plans.find((p) => p.name === planName);
    if (main) return main;
    if (oneTimeStudy.name === planName) return oneTimeStudy;
    if (studentPlan.name === planName) return studentPlan;
    return null;
  };

  useEffect(() => {
    if (userData) {
      setEditedName(userData.username || "");
      const validProjects =
        userData.projectAllowed?.filter(
          (p) => p.status === "completed" || p.status === "running",
        ) || [];
      setProjects(validProjects);
      const matched = findPlanByName(userData.planDetails?.plan_name);
      setPlanDetails(matched || null);
    }
  }, [userData]);

  const handleProfilePicUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploadingPic(true);
    const token = Cookies.get("token");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const uploadRes = await fetch(
        `${process.env.REACT_APP_API_URL}/user/uploadProfilePic`,
        {
          method: "POST",
          headers: { token: token },
          body: formData,
          credentials: "include",
        },
      );
      const uploadData = await uploadRes.json();
      if (uploadData.success) {
        const { keys, bucket } = uploadData.data;
        const updateRes = await fetch(
          `${process.env.REACT_APP_API_URL}/user/editUser`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", token: token },
            body: JSON.stringify({
              first_name: userData.username,
              profile_pic: keys,
            }),
            credentials: "include",
          },
        );
        const updateData = await updateRes.json();
        if (updateData.success) {
          updateUser({ profile_pic: keys });
        }
      }
    } catch (err) {
      console.error("Error uploading profile picture:", err);
      alert("Failed to upload profile picture");
    } finally {
      setIsUploadingPic(false);
    }
  };

  const handleNameUpdate = async () => {
    if (!editedName.trim() || editedName === userData.username) {
      setIsEditingName(false);
      setEditedName(userData.username);
      return;
    }
    const token = Cookies.get("token");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/user/editUser`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", token: token },
          body: JSON.stringify({
            first_name: editedName,
            profile_pic: userData.profile_pic,
          }),
          credentials: "include",
        },
      );
      const data = await res.json();
      if (data.success) {
        updateUser({ username: editedName });
        setIsEditingName(false);
      } else {
        alert("Failed to update name");
      }
    } catch (err) {
      console.error("Error updating name:", err);
      alert("Failed to update name");
    }
  };

  const handleChangePassword = async () => {
    const newErrors = {};
    if (!oldPassword) newErrors.oldPassword = "Current password is required";
    if (!newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (newPassword.length < 8 || newPassword.length > 16) {
      newErrors.newPassword = "Password must be between 8 and 16 characters";
    } else if (!/[a-z]/.test(newPassword)) {
      newErrors.newPassword =
        "Password must contain at least one lowercase letter";
    } else if (!/[A-Z]/.test(newPassword)) {
      newErrors.newPassword =
        "Password must contain at least one uppercase letter";
    } else if (!/[0-9]/.test(newPassword)) {
      newErrors.newPassword = "Password must contain at least one digit";
    } else if (!/[!@#$%^&*]/.test(newPassword)) {
      newErrors.newPassword =
        "Password must contain at least one special character (!@#$%^&*)";
    } else if (oldPassword && newPassword === oldPassword) {
      newErrors.newPassword =
        "New password must be different from current password";
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    const token = Cookies.get("token");
    if (!token) return;
    try {
      setIsUpdatingPassword(true);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/user/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json", token: token },
        body: JSON.stringify({
          old_password: oldPassword,
          new_password: newPassword,
        }),
      });
      const data = await res.json();
      if (data.success || data.status === "success") {
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setErrors({});
        setPasswordSuccess(true);
        setTimeout(() => setPasswordSuccess(false), 3000);
      } else {
        setErrors({ apiError: data.message || "Failed to update password" });
      }
    } catch {
      setErrors({ apiError: "Something went wrong. Please try again." });
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleDeactivateAccount = async () => {
    setIsDeactivating(true);
    setDeactivationError("");
    setShowDeactivateDialog(false);
    const token = Cookies.get("token");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/user/deactivateUser`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", token: token },
          body: JSON.stringify({
            userId: session?.user?._id,
            email: session?.user?.email,
          }),
        },
      );
      const data = await res.json();
      if (data.success) {
        await refreshUser();
        setDeactivationError("");
      } else {
        setDeactivationError(data.message || "Failed to deactivate account");
        setTimeout(() => setDeactivationError(""), 5000);
      }
    } catch (err) {
      console.error("Error deactivating account:", err);
      setDeactivationError("Failed to deactivate account. Please try again.");
      setTimeout(() => setDeactivationError(""), 5000);
    } finally {
      setIsDeactivating(false);
    }
  };
  const handleDeactivateOrg = async () => {
    setIsOrgDeactivating(true);
    setOrgDeactivationError("");
    setShowDeactivateOrgDialog(false);
    const token = Cookies.get("token");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/user/org/deactivateOrg`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", token: token },
          body: JSON.stringify({
            orgId: orgId,
            orgName: userData.org_name,
          }),
        },
      );
      const data = await res.json();
      if (data.success) {
        setIsOrgDeactivated("PENDING");
        await refreshUser();
      } else {
        setOrgDeactivationError(
          data.message || "Failed to deactivate organisation",
        );
        setTimeout(() => setOrgDeactivationError(""), 5000);
      }
    } catch (err) {
      console.error("Error deactivating org:", err);
      setOrgDeactivationError(
        "Failed to deactivate organisation. Please try again.",
      );
      setTimeout(() => setOrgDeactivationError(""), 5000);
    } finally {
      setIsOrgDeactivating(false);
    }
  };

  const handleCancelOrgDeactivation = async () => {
    setIsCancelingOrgDeactivation(true);
    setOrgDeactivationError("");
    setShowCancelOrgDialog(false);
    const token = Cookies.get("token");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/user/org/cancel-deactivation`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", token: token },
          body: JSON.stringify({
            orgId: orgId,
            orgName: userData.org_name,
          }),
        },
      );
      const data = await res.json();
      if (data.success) {
        setIsOrgDeactivated(null);
        await refreshUser();
      } else {
        setOrgDeactivationError(
          data.message || "Failed to cancel org deactivation",
        );
        setTimeout(() => setOrgDeactivationError(""), 5000);
      }
    } catch (err) {
      console.error("Error canceling org deactivation:", err);
      setOrgDeactivationError(
        "Failed to cancel org deactivation. Please try again.",
      );
      setTimeout(() => setOrgDeactivationError(""), 5000);
    } finally {
      setIsCancelingOrgDeactivation(false);
    }
  };
  if (!userData) {
    return (
      <div style={{ color: "white", textAlign: "center", padding: "2rem" }}>
        No profile data found.
      </div>
    );
  }
  const name = userData.username || "User";
  const email = session?.user?.email || "N/A";
  const orgName = userData.org_name || "Unknown";
  const isActive = userData.active_status ? "Active" : "Inactive";
  const memberSince = userData.create_date
    ? new Date(userData.create_date).toLocaleDateString()
    : "N/A";
  const planName = userData.planDetails?.plan_name || "No Active Plan";
  const totalProjectsAllowed = userData.projectAllowed?.length;
  const totalCredits = userData.planDetails?.total_credits_score || 0;
  const creditsLeft = userData.planDetails?.credits_score || 0;
  const usedCredits = Math.max(totalCredits - creditsLeft, 0);
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();
  const showDeactivateOrgBtn = isOrgDeactivated === null;
  const showCancelOrgBtn = isOrgDeactivated?.toUpperCase() === "PENDING";
  const isOrgDeactivationComplete = isOrgDeactivated?.toUpperCase() === "COMPLETED";

  return (
    <div className="admin-container">
      <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
        {/* Profile Header */}
        <div className="admin-card profile-header-card">
          <div className="profile-header-content">
            <div className="profile-header-main">
              <div className="profile-avatar-container">
                {profilePicUrl ? (
                  <img
                    src={profilePicUrl}
                    alt="Profile"
                    className="admin-avatar profile-avatar-img"
                  />
                ) : (
                  <div className="admin-avatar">{initials}</div>
                )}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingPic}
                  className="profile-camera-btn"
                >
                  <CameraAlt className="profile-camera-icon" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePicUpload}
                  className="profile-file-input"
                />
              </div>
              <div>
                <div className="profile-name-section">
                  {isEditingName ? (
                    <div className="profile-name-edit-container">
                      <div className="profile-name-edit-row">
                        <input
                          type="text"
                          value={editedName}
                          onChange={(e) => {
                            const value = e.target.value;
                            const lengthWithoutSpaces = value.replace(
                              /\s/g,
                              "",
                            ).length;
                            if (lengthWithoutSpaces > NAME_MAX_LENGTH) {
                              setNameError(
                                `Name cannot exceed ${NAME_MAX_LENGTH} characters (excluding spaces)`,
                              );
                              return;
                            }
                            setNameError("");
                            setEditedName(value);
                          }}
                          className="profile-name-input"
                        />
                        <button
                          onClick={handleNameUpdate}
                          disabled={!!nameError}
                          className="primary-btn profile-name-save-btn"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditedName(userData.username);
                            setIsEditingName(false);
                          }}
                          className="secondary-btn profile-name-cancel-btn"
                        >
                          Cancel
                        </button>
                      </div>
                      {nameError && (
                        <p className="profile-name-error">{nameError}</p>
                      )}
                    </div>
                  ) : (
                    <>
                      <h2 className="profile-name-display">{name}</h2>
                      <button
                        onClick={() => {
                          setEditedName(userData.username || "");
                          setNameError("");
                          setIsEditingName(true);
                        }}
                        className="profile-name-edit-btn"
                      >
                        <Edit2 size={16} color="#9ca3af" />
                      </button>
                    </>
                  )}
                </div>
                <p className="profile-email">{email}</p>
                <div className="profile-badges">
                  <span className="profile-badge-role">{roleName}</span>
                  <span className="profile-badge-status">{isActive}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="profile-stats-grid">
          <div className="stat-card">
            <div className="stat-header">
              <span>Your Projects</span>
              <FolderOpen size={18} style={{ color: "#06b6d4" }} />
            </div>
            <p className="stat-value">{userData.project_count || 0}</p>
          </div>
          <div className="stat-card">
            <div className="stat-header">
              <span>Active Projects in Org</span>
              <FolderOpen size={18} style={{ color: "#06b6d4" }} />
            </div>
            <p className="stat-value">{totalProjectsAllowed}</p>
          </div>
          <div className="stat-card">
            <div className="stat-header">
              <span>Current Plan</span>
              <Crown size={18} style={{ color: "#fbbf24" }} />
            </div>
            <p className="stat-value">{planName}</p>
            <p style={{ color: "#9ca3af", fontSize: "0.75rem" }}>
              {planDetails?.priceDisplay || ""}
            </p>
          </div>
        </div>
        <div className="admin-card">
          <h3 className="profile-account-info-title">Account Information</h3>
          <div className="profile-info-rows">
            <InfoRow label="Organisation" value={orgName} />
            <InfoRow label="Member Since" value={memberSince} />
            <InfoRow
              label="Research Units Used"
              value={
                totalCredits
                  ? `${usedCredits.toLocaleString()} / ${totalCredits.toLocaleString()}`
                  : "—"
              }
            />
          </div>
        </div>

        {/* Change Password */}
        <div className="admin-card">
          <h3 className="password-section-title">Change Password</h3>

          {passwordSuccess && (
            <div className="password-success-message">
              Your password has been changed successfully!
            </div>
          )}

          <div className="password-fields-container">
            <div className="password-field-wrapper">
              <div className="password-input-container">
                <input
                  type={showOldPassword ? "text" : "password"}
                  placeholder="Current Password"
                  value={oldPassword}
                  onChange={(e) => {
                    setOldPassword(e.target.value);
                    setErrors({ ...errors, oldPassword: "" });
                  }}
                  className={`password-input ${errors.oldPassword ? "error" : ""}`}
                />
                <button
                  type="button"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                  className="password-toggle-btn"
                >
                  {showOldPassword ? (
                    <FaEye size={16} />
                  ) : (
                    <FaEyeSlash size={16} />
                  )}
                </button>
              </div>
              {errors.oldPassword && (
                <p className="password-field-error">{errors.oldPassword}</p>
              )}
            </div>

            <div className="password-field-wrapper">
              <div className="password-input-container">
                <input
                  type={showNewPassword ? "text" : "password"}
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setErrors({ ...errors, newPassword: "" });
                  }}
                  className={`password-input ${errors.newPassword ? "error" : ""}`}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="password-toggle-btn"
                >
                  {showNewPassword ? (
                    <FaEye size={16} />
                  ) : (
                    <FaEyeSlash size={16} />
                  )}
                </button>
              </div>
              {errors.newPassword && (
                <p className="password-field-error">{errors.newPassword}</p>
              )}
            </div>

            <div className="password-field-wrapper">
              <div className="password-input-container">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setErrors({ ...errors, confirmPassword: "" });
                  }}
                  className={`password-input ${errors.confirmPassword ? "error" : ""}`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="password-toggle-btn"
                >
                  {showConfirmPassword ? (
                    <FaEye size={16} />
                  ) : (
                    <FaEyeSlash size={16} />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="password-field-error">{errors.confirmPassword}</p>
              )}
            </div>

            <button
              onClick={handleChangePassword}
              disabled={isUpdatingPassword}
              className="password-save-btn"
            >
              {isUpdatingPassword ? "Saving..." : "Save"}
            </button>
          </div>

          {errors.apiError && (
            <p className="password-api-error">{errors.apiError}</p>
          )}
        </div>
        {deactivationError && (
          <p
            style={{
              color: "#ef4444",
              fontSize: "0.875rem",
              textAlign: "center",
            }}
          >
            {deactivationError}
          </p>
        )}
        <div className="account-actions-buttons">
          <button
            onClick={() => setShowDeactivateDialog(true)}
            disabled={isDeactivating}
            className="deactivate-btn"
          >
            {isDeactivating ? "Deactivating..." : "Deactivate Account"}
          </button>

          {showDeactivateOrgBtn && (
            <button
              onClick={() => setShowDeactivateOrgDialog(true)}
              disabled={isOrgDeactivating}
              className="deactivate-btn"
            >
              {isOrgDeactivating ? "Deactivating..." : "Deactivate Org"}
            </button>
          )}

          {showCancelOrgBtn && (
            <button
              onClick={() => setShowCancelOrgDialog(true)}
              disabled={isCancelingOrgDeactivation}
              className="deactivate-btn deactivate-btn--cancel"
               style={{
                backgroundColor: "#22c55e",
                borderColor: "#22c55e",
                color: "#ffffff",
              }}
            >
              {isCancelingOrgDeactivation
                ? "Canceling..."
                : "Cancel Org Deactivation"}
            </button>
          )}
        </div>
        {orgDeactivationError && (
          <p
            style={{
              color: "#ef4444",
              fontSize: "0.875rem",
              textAlign: "center",
            }}
          >
            {orgDeactivationError}
          </p>
        )}
        {isOrgDeactivated?.toUpperCase() === "PENDING" && (
          <div
            style={{
              background: "rgba(245, 158, 11, 0.1)",
              border: "1px solid #f59e0b",
              borderRadius: "8px",
              padding: "0.75rem 1rem",
              color: "#f59e0b",
              fontSize: "0.875rem",
              textAlign: "center",
            }}
          >
            Your organisation is scheduled for deactivation. All members will be
            logged out once it completes.
          </div>
        )}
      </div>
      <Dialog
        open={showDeactivateDialog}
        onClose={() => {}}
        sx={{
          "& .MuiBackdrop-root": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#000000",
            border: "2px solid #ef4444",
            borderRadius: "8px",
            color: "white",
            width: "450px",
            maxWidth: "90vw",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#000000",
            color: "white",
            textAlign: "center",
            fontSize: "1.3rem",
            fontWeight: "bold",
            paddingBottom: "10px",
          }}
        >
          Deactivate Account?
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: "#000000",
            color: "white",
            textAlign: "center",
            padding: "20px",
          }}
        >
          <Typography sx={{ fontSize: "1rem", mb: 2 }}>
            Are you sure you want to deactivate your account? This will take
            effect immediately and you will be logged out.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: "#000000",
            justifyContent: "center",
            paddingBottom: "20px",
            gap: "12px",
          }}
        >
          <Button
            onClick={() => setShowDeactivateDialog(false)}
            sx={{
              color: "#666",
              border: "1px solid #444",
              padding: "10px 30px",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                borderColor: "#666",
              },
            }}
          >
            CANCEL
          </Button>
          <Button
            onClick={handleDeactivateAccount}
            variant="contained"
            sx={{
              backgroundColor: "#ef4444",
              color: "white",
              padding: "10px 30px",
              "&:hover": { backgroundColor: "#dc2626" },
            }}
          >
            DEACTIVATE
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showDeactivateOrgDialog}
        onClose={() => {}}
        sx={{
          "& .MuiBackdrop-root": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#000000",
            border: "2px solid #ef4444",
            borderRadius: "8px",
            color: "white",
            width: "450px",
            maxWidth: "90vw",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#000000",
            color: "white",
            textAlign: "center",
            fontSize: "1.3rem",
            fontWeight: "bold",
            paddingBottom: "10px",
          }}
        >
          Deactivate Organisation?
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: "#000000",
            color: "white",
            textAlign: "center",
            padding: "20px",
          }}
        >
          <Typography sx={{ fontSize: "1rem", mb: 2 }}>
            Are you sure you want to deactivate <strong>{orgName}</strong>? All
            members of the organisation will be logged out once deactivation
            completes.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: "#000000",
            justifyContent: "center",
            paddingBottom: "20px",
            gap: "12px",
          }}
        >
          <Button
            onClick={() => setShowDeactivateOrgDialog(false)}
            sx={{
              color: "#666",
              border: "1px solid #444",
              padding: "10px 30px",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                borderColor: "#666",
              },
            }}
          >
            CANCEL
          </Button>
          <Button
            onClick={handleDeactivateOrg}
            variant="contained"
            sx={{
              backgroundColor: "#ef4444",
              color: "white",
              padding: "10px 30px",
              "&:hover": { backgroundColor: "#dc2626" },
            }}
          >
            DEACTIVATE ORG
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showCancelOrgDialog}
        onClose={() => {}}
        sx={{
          "& .MuiBackdrop-root": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#000000",
            border: "2px solid #22c55e",
            borderRadius: "8px",
            color: "white",
            width: "450px",
            maxWidth: "90vw",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#000000",
            color: "white",
            textAlign: "center",
            fontSize: "1.3rem",
            fontWeight: "bold",
            paddingBottom: "10px",
          }}
        >
          Cancel Org Deactivation?
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: "#000000",
            color: "white",
            textAlign: "center",
            padding: "20px",
          }}
        >
          <Typography sx={{ fontSize: "1rem", mb: 2 }}>
            This will cancel the scheduled deactivation for{" "}
            <strong>{orgName}</strong> and keep all members active.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: "#000000",
            justifyContent: "center",
            paddingBottom: "20px",
            gap: "12px",
          }}
        >
          <Button
            onClick={() => setShowCancelOrgDialog(false)}
            sx={{
              color: "#666",
              border: "1px solid #444",
              padding: "10px 30px",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                borderColor: "#666",
              },
            }}
          >
            CLOSE
          </Button>
          <Button
            onClick={handleCancelOrgDeactivation}
            variant="contained"
            sx={{
              backgroundColor: "#22c55e",
              color: "white",
              padding: "10px 30px",
              "&:hover": { backgroundColor: "#22c55e71" },
            }}
          >
            CONFIRM CANCEL
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const InfoRow = ({ label, value, color = "white" }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      padding: "0.5rem 0",
      borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
    }}
  >
    <span style={{ fontSize: "0.875rem", color: "#9ca3af" }}>{label}</span>
    <span style={{ fontSize: "0.875rem", color: color, fontWeight: 500 }}>
      {value}
    </span>
  </div>
);

export default Profile;